import { useState, useEffect, useCallback } from "react";
import { LockOutlined } from "@ant-design/icons";
import { BarChart2, AlertCircle } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import { useAllAnalytics } from "../../hooks/useAnalyticsQueries";
import AnalyticsHeader from "./AnalyticsHeader";
import AnalyticsForm   from "./AnalyticsForm";
import AnalyticsTabs, { TabId } from "./AnalyticsTabs";
import OverviewTab  from "./tabs/OverviewTab";
import TrafficTab   from "./tabs/TrafficTab";
import DevicesTab   from "./tabs/DevicesTab";
import PagesTab     from "./tabs/PagesTab";
import CitiesTab    from "./tabs/CitiesTab";
import TrendsTab    from "./tabs/TrendsTab";
import { todayStr, ago30Str } from "./utils/dateHelpers";

 const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4 px-6 py-6">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-20 rounded-2xl bg-slate-100" />
      ))}
    </div>
    <div className="h-56 rounded-2xl bg-slate-100" />
    <div className="grid grid-cols-2 gap-3">
      <div className="h-40 rounded-2xl bg-slate-100" />
      <div className="h-40 rounded-2xl bg-slate-100" />
    </div>
  </div>
);

 
const AuthGate: React.FC = () => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12
    flex flex-col items-center gap-5 text-center">
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center
      bg-gradient-to-br from-[#0F52BA] to-blue-400 text-white text-2xl
      shadow-md shadow-blue-200">
      <LockOutlined />
    </div>
    <div>
      <h3 className="text-[15px] font-bold text-slate-800">Authentification requise</h3>
      <p className="text-[12px] text-slate-400 mt-1">Connectez-vous pour accéder aux Analytics</p>
    </div>
  </div>
);

 const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-6">
    <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center
      border border-slate-100 mb-1">
      <BarChart2 size={24} className="text-slate-300" />
    </div>
    <p className="text-[13px] font-semibold text-slate-600">Aucune donnée disponible</p>
    <p className="text-[11px] text-slate-400">Vérifiez votre GA ID et réessayez</p>
  </div>
);

 const ErrorBanner: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="mx-6 mb-2 flex items-center gap-3 px-4 py-3 rounded-xl
    bg-rose-50 border border-rose-100 text-rose-700">
    <AlertCircle size={15} className="flex-shrink-0" />
    <p className="text-[12px] font-medium flex-1">{message}</p>
    <button
      onClick={onRetry}
      className="text-[11px] font-semibold px-3 py-1 rounded-lg bg-rose-100
        hover:bg-rose-200 transition-colors"
    >
      Réessayer
    </button>
  </div>
);

 const GoogleAnalyticsAdvanced: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [gaId,      setGaId]      = useState(() => localStorage.getItem("ga-property-id") || "GA-263109122");
  const [startDate, setStartDate] = useState(ago30Str);
  const [endDate,   setEndDate]   = useState(todayStr);
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const [committedGaId,  setCommittedGaId]  = useState(gaId);
  const [committedStart, setCommittedStart] = useState(startDate);
  const [committedEnd,   setCommittedEnd]   = useState(endDate);

  useEffect(() => { localStorage.setItem("ga-property-id", gaId); }, [gaId]);

  const analyticsQuery = useAllAnalytics(
    committedGaId, committedStart, committedEnd,
    isAuthenticated && !!committedGaId && !!committedStart && !!committedEnd,
  );

  const allData = analyticsQuery.data ?? null;
  const loading = analyticsQuery.isFetching;
  const error   = analyticsQuery.isError
    ? (analyticsQuery.error as any)?.response?.data?.message || "Erreur lors du chargement"
    : null;

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setCommittedGaId(gaId);
    setCommittedStart(startDate);
    setCommittedEnd(endDate);
    analyticsQuery.refetch();
  }, [gaId, startDate, endDate, analyticsQuery]);

  const handleRefresh = useCallback(() => analyticsQuery.refetch(), [analyticsQuery]);

   if (authLoading) return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm
      flex items-center justify-center gap-3 py-16 text-slate-400">
      <div className="h-7 w-7 rounded-full border-[3px] border-blue-100 border-t-blue-500 animate-spin" />
      <span className="text-[12px] font-medium">Vérification...</span>
    </div>
  );

  if (!isAuthenticated) return <AuthGate />;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

       <div className="h-[3px] bg-gradient-to-r from-orange-600 via-orange-400 to-orange-200" />

       <div className="px-6 pt-5 pb-4 space-y-4">
        <AnalyticsHeader loading={loading} onRefresh={handleRefresh} />
        <AnalyticsForm
          gaId={gaId}
          startDate={startDate}
          endDate={endDate}
          loading={loading}
          error={null}
          onGaIdChange={setGaId}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSubmit={handleSubmit}
          onDismissError={handleRefresh}
        />
      </div>

       {error && <ErrorBanner message={error} onRetry={handleRefresh} />}

       {loading && <LoadingSkeleton />}

       {!loading && !error && allData && (
        <div className="px-6 pb-6">
           <div className="h-px bg-slate-100 mb-5" />

          <AnalyticsTabs activeTab={activeTab} onTabChange={setActiveTab} />

          <div className="mt-5">
            {activeTab === "overview"  && <OverviewTab  data={allData.overview} />}
            {activeTab === "traffic"   && <TrafficTab   data={allData.trafficSources  || []} />}
            {activeTab === "devices"   && <DevicesTab   data={allData.devices         || []} />}
            {activeTab === "pages"     && <PagesTab     data={allData.topPages        || []} />}
            {activeTab === "cities"    && <CitiesTab    data={allData.usersByCity      || []} />}
            {activeTab === "trends"    && <TrendsTab    data={allData.dailyTrends     || []} />}
          </div>
        </div>
      )}

       {!loading && !error && !allData && <EmptyState />}
    </div>
  );
};

export default GoogleAnalyticsAdvanced;